import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  TrackChangesOutlined,
  SupportAgentOutlined,
  PersonOutline,
  ReceiptLongOutlined,
  CalculateOutlined,
  SmartToyOutlined,
  EmailOutlined,
  LockOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import AdminCompaniesModal from '../../src/components/Gestao/AdminCompaniesModal';

const theme = createTheme({
  palette: {
    primary: { main: '#168df2' },
    secondary: { main: '#9b60ff' },
  },
  typography: {
    fontFamily: 'Futura, Arial, sans-serif',
    button: { textTransform: 'none' },
  },
});

const operationModules = [
  { label: 'Pré-venda', icon: TrackChangesOutlined },
  { label: 'Pós-venda', icon: SupportAgentOutlined },
  { label: 'Corretor', icon: PersonOutline },
  { label: 'Inadimplência', icon: ReceiptLongOutlined },
  { label: 'Contabilidade', icon: CalculateOutlined },
  { label: 'Agentes', icon: SmartToyOutlined },
];

const loginFieldSx = {
  mt: { xs: 2, lg: 1.25 },
  '& .MuiInputLabel-root': { color: '#c5c9eb' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#a673ff' },
  '& .MuiOutlinedInput-root': {
    color: '#f7f8ff',
    borderRadius: 1,
    bgcolor: '#0c1330',
    '& fieldset': { borderColor: 'rgba(132, 145, 211, 0.35)' },
    '&:hover fieldset': { borderColor: 'rgba(159, 112, 255, 0.82)' },
    '&.Mui-focused fieldset': { borderColor: '#9b60ff' },
  },
  '& input::placeholder': { color: '#7e87aa', opacity: 1 },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: '0 0 0 100px #0c1330 inset',
    WebkitTextFillColor: '#f7f8ff',
    caretColor: '#f7f8ff',
  },
};

const Home = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();

  const isCvcrmBrand =
    typeof window !== 'undefined' && localStorage.getItem('brand') === 'cvcrm';

  const handleChange = (event) => {
    setCredentials({ ...credentials, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(`${apiUrl}/login`, {
        email: credentials.email,
        senha: credentials.password,
      });

      const token = response.data;
      localStorage.setItem('jwtToken', token);

      const decodedToken = jwtDecode(token);
      const userRole = decodedToken.role;

      if (userRole === 'corretor') {
        setTimeout(() => navigate('/area-corretor'), 1500);
      } else if (userRole === 'setup' || userRole === 'aipim') {
        try {
          setIsLoadingCompanies(true);
          const companiesResponse = await axios.get(`${apiUrl}/empresas`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setCompanies(companiesResponse.data.map((company) => ({
            id: company.idCliente,
            name: company.nomeCliente,
            dataHoraRegistro: company.dataHoraRegistro,
          })));
          setIsLoading(false);
          setIsLoadingCompanies(false);
          setOpenModal(true);
        } catch (companyError) {
          console.error('Erro ao buscar empresas:', companyError);
          alert('Erro ao carregar lista de empresas.');
          setIsLoading(false);
          setIsLoadingCompanies(false);
        }
      } else {
        setTimeout(() => navigate('/home_logado'), 1500);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Erro ao fazer login:', error);
      alert('Erro ao fazer login. Verifique suas credenciais.');
    }
  };

  const handleSelectCompany = async (company) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(
        `${apiUrl}/login/integrador`,
        { idCliente: company.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      localStorage.setItem('jwtToken', response.data.token);
      localStorage.setItem('selectedCompany', JSON.stringify(company));
      navigate('/home_logado');
      setOpenModal(false);
    } catch (error) {
      console.error('Erro ao selecionar empresa:', error);
      alert('Erro ao selecionar empresa. Tente novamente.');
      setOpenModal(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#070c20', color: '#f7f8ff', overflow: { lg: 'hidden' }, position: 'relative', '@media (min-width: 1200px)': { height: '100dvh' } }} aria-label="Página de login do Portal do Cliente">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: { xs: 68, lg: 64 }, px: { xs: 3, md: 7 }, py: { xs: 2, lg: 1.5 }, borderBottom: '1px solid rgba(187, 138, 255, 0.18)', background: 'linear-gradient(105deg, #340346 0%, #190637 48%, #0a1028 100%)', position: 'relative', zIndex: 1 }}>
          <Box component="img" src={isCvcrmBrand ? '/logoCV-black.svg' : '/aipim-tech-logo-white.png'} alt={isCvcrmBrand ? 'CV CRM' : 'Aipim.tech'} sx={{ width: { xs: 150, sm: 190, md: 222 }, height: 'auto', display: 'block', ...(isCvcrmBrand ? { filter: 'brightness(0) invert(1)' } : {}) }} />
          <Button component="a" href="https://www.aipim.tech/" target="_blank" rel="noreferrer" endIcon={<OpenInNewOutlined fontSize="small" />} sx={{ color: '#cabfff', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}>
            Site Aipim
          </Button>
        </Box>

        <Box sx={{ minHeight: 'calc(100vh - 68px)', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 3, md: 7 }, py: { xs: 5, lg: 2 }, position: 'relative', bgcolor: '#070c20', backgroundImage: "linear-gradient(112deg, rgba(75, 4, 92, 0.78) 0%, rgba(43, 8, 76, 0.66) 36%, rgba(12, 14, 44, 0.4) 66%, rgba(7, 12, 32, 0.18) 100%), url('/portal-circuit-background.png')", backgroundPosition: { xs: 'center', lg: 'center, center 56px' }, backgroundRepeat: 'no-repeat', backgroundSize: { xs: 'cover', lg: 'cover, 100% calc(100% - 56px)' }, '@media (min-width: 1200px)': { minHeight: 'calc(100dvh - 64px)' } }}>

          <Box sx={{ width: '100%', maxWidth: 1280, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(390px, 520px)' }, columnGap: { xs: 0, lg: 7 }, rowGap: { xs: 3, lg: 0 }, alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, order: 0 }}>
              <Typography sx={{ color: '#9a65ff', fontSize: 14, fontWeight: 800, letterSpacing: 1.2, mb: 1.5 }}>PORTAL DO CLIENTE</Typography>
              <Typography component="h1" sx={{ fontSize: 42, lineHeight: 1.04, letterSpacing: 0, fontWeight: 800, mb: 1.5 }}>
                Central de <Box component="span" sx={{ color: '#9a65ff' }}>Operações</Box>
              </Typography>
              <Typography sx={{ color: '#c5c9eb', fontSize: 18, lineHeight: 1.45 }}>
                Acompanhe agentes, módulos, indicadores e tarefas em um só lugar.
              </Typography>
            </Box>

            <Box sx={{ order: { xs: 2, lg: 1 } }}>
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Typography sx={{ color: '#9a65ff', fontSize: 14, fontWeight: 800, letterSpacing: 1.2, mb: 1 }}>PORTAL DO CLIENTE</Typography>
                <Typography component="h1" sx={{ fontSize: { md: 52 }, lineHeight: 1.04, letterSpacing: 0, fontWeight: 800, mb: 1.25, maxWidth: 580 }}>
                  Central de <Box component="span" sx={{ color: '#9a65ff' }}>Operações</Box>
                </Typography>
                <Typography sx={{ color: '#c5c9eb', fontSize: 18, lineHeight: 1.4, maxWidth: 560, mb: 2 }}>
                  Acompanhe agentes, módulos, indicadores e tarefas em um só lugar.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 1.5, lg: 1.25 }, maxWidth: 560 }}>
                {operationModules.map(({ label, icon: Icon }) => (
                  <Box key={label} sx={{ minHeight: { xs: 86, lg: 64 }, display: 'flex', alignItems: 'center', gap: { xs: 2, lg: 1.25 }, px: { xs: 2.5, lg: 1.75 }, py: { xs: 2, lg: 1 }, border: '1px solid rgba(172, 128, 240, 0.3)', borderRadius: 1, bgcolor: 'rgba(27, 19, 66, 0.74)' }}>
                    <Icon sx={{ color: '#9b60ff', fontSize: { xs: 36, lg: 28 }, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: 18, lg: 15 }, fontWeight: 700 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#c5c9eb', mt: { xs: 3.5, lg: 1.5 } }}>
                <Box component="img" src="/portal-security-shield.svg" alt="" sx={{ width: { xs: 34, lg: 30 }, height: { xs: 40, lg: 35 }, flexShrink: 0 }} />
                <Typography sx={{ fontSize: { xs: 16, lg: 15 } }}>Acesso seguro e personalizado para sua operação.</Typography>
              </Box>
            </Box>

            <Box component="form" onSubmit={handleSubmit} aria-label="Formulário de login" sx={{ order: { xs: 1, lg: 2 }, border: '1px solid rgba(172, 128, 240, 0.38)', borderRadius: 1, bgcolor: 'rgba(20, 14, 57, 0.94)', px: { xs: 3, sm: 5, lg: 3.5 }, py: { xs: 4, sm: 5, lg: 2.75 }, boxShadow: '0 24px 64px rgba(0, 0, 0, 0.3)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 4, lg: 2 } }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: { xs: 54, lg: 48 }, height: { xs: 54, lg: 48 }, color: '#9b60ff', border: '1px solid rgba(155, 96, 255, 0.42)', borderRadius: 1 }}><LockOutlined fontSize="large" /></Box>
                <Typography component="h2" sx={{ fontSize: { xs: 26, sm: 30, lg: 27 }, fontWeight: 800 }}>Acesse o Portal</Typography>
              </Box>
              <TextField label="E-mail" name="email" type="email" size="small" fullWidth variant="outlined" value={credentials.email} onChange={handleChange} required disabled={isLoading} placeholder="seu@email.com" aria-label="Digite seu e-mail" InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: '#c5c9eb' }} /></InputAdornment> }} sx={loginFieldSx} />
              <TextField label="Senha" name="password" type={showPassword ? 'text' : 'password'} size="small" fullWidth variant="outlined" value={credentials.password} onChange={handleChange} required disabled={isLoading} placeholder="Digite sua senha" aria-label="Digite sua senha" InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: '#c5c9eb' }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><Tooltip title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}><IconButton aria-label="Alternar visibilidade da senha" onClick={() => setShowPassword((visible) => !visible)} onMouseDown={(event) => event.preventDefault()} edge="end" disabled={isLoading} sx={{ color: '#c5c9eb' }}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></Tooltip></InputAdornment> }} sx={loginFieldSx} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0 }}><Button onClick={() => alert('Função de recuperação de senha em breve.')} disabled={isLoading} sx={{ color: '#1fa1ff', fontWeight: 600, py: 0.25, fontSize: 14 }}>Esqueci minha senha</Button></Box>
              <Button type="submit" variant="contained" disabled={isLoading} fullWidth sx={{ mt: { xs: 3, lg: 1.5 }, minHeight: { xs: 58, lg: 48 }, borderRadius: 1, bgcolor: '#168df2', fontWeight: 800, fontSize: { xs: 17, lg: 16 }, '&:hover': { bgcolor: '#0878d4' }, '&:disabled': { bgcolor: '#45648a' } }}>
                {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Entrar no portal'}
              </Button>
              <Box sx={{ borderTop: '1px solid rgba(132, 145, 211, 0.2)', mt: { xs: 4, lg: 2 }, pt: { xs: 3.5, lg: 2 }, display: 'flex', alignItems: 'center', gap: 1.5, color: '#c5c9eb' }}>
                <PersonOutline sx={{ color: '#9b60ff', fontSize: { xs: 32, lg: 28 } }} />
                <Typography sx={{ fontSize: { xs: 16, lg: 15 } }}>Ainda não possui acesso?<br /><Box component="span" sx={{ color: '#b786ff' }}>Fale com seu gestor.</Box></Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <AdminCompaniesModal open={openModal} onClose={() => { setOpenModal(false); setIsLoading(false); }} companies={companies} onSelectCompany={handleSelectCompany} isLoading={isLoadingCompanies} />
    </ThemeProvider>
  );
};

export default Home;
